import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  BadgeCheck, ClipboardList, FileText, FolderOpen, IdCard, Inbox, Landmark, RefreshCw, Thermometer, UserRound,
} from 'lucide-react';
import { api } from '../lib/api';
import { formatDate } from '../lib/utils';
import { Card, EmptyState, ICON, Kpi, ListRow, Skeleton } from './ui';

/** Stukken die de app al als document heeft (Documenten > Per jaar). */
const AL_IN_APP = /^(Uren bijlage)$/;

const d = (iso: string | null | undefined) => (iso ? formatDate(iso) : '–');
const statusCls = (s: string | null) =>
  !s ? 'status-muted' : /afgehandeld|akkoord|goedgekeurd/i.test(s) ? 'status-ok' : /afgewezen/i.test(s) ? 'status-err' : 'status-warn';

/**
 * Wat de app uit AFAS InSite haalt buiten loonstroken en urenbijlagen: open taken, ingestuurde
 * verzoeken, het dossier, verzuim, ID-bewijs/VOG, loonheffingskorting en opleidingen.
 * Vanzelf om de twee dagen bijgewerkt (of via Acties > AFAS ophalen).
 */
export function AfasDossier() {
  const { data, isLoading } = useQuery({ queryKey: ['afas-portal'], queryFn: api.afas.portal, staleTime: 10 * 60_000 });
  const [allesDossier, setAllesDossier] = useState(false);
  const [allesIngestuurd, setAllesIngestuurd] = useState(false);

  if (isLoading)
    return (
      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="card space-y-3">
            <Skeleton className="h-3 w-20" />
            <Skeleton className="h-7 w-24" />
          </div>
        ))}
      </div>
    );
  if (!data || !Object.keys(data).length)
    return (
      <Card>
        <EmptyState icon={RefreshCw} title="Nog niets uit AFAS opgehaald" text="Dat gebeurt vanzelf om de twee dagen, of via Acties, AFAS ophalen." />
      </Card>
    );

  const taken = data.taken?.rows ?? [];
  const ingestuurd = data.ingestuurd?.rows ?? [];
  const lopend = ingestuurd.filter((r) => r.status && !/afgehandeld/i.test(r.status));
  const dossier = (data.dossier?.rows ?? []).filter((r) => !AL_IN_APP.test(r.soort ?? ''));
  const verzuim = data.verzuim?.rows ?? [];
  const id = data.persoonsdocumenten?.rows?.[0];
  const lhk = (data.belastingdienst?.rows ?? []).find((r) => !r.tot) ?? data.belastingdienst?.rows?.at(-1);
  const opleidingen = [...(data.opleidingen?.rows ?? []), ...(data.cursussen?.rows ?? []), ...(data.competenties?.rows ?? [])];
  const vog = data.vog?.rows ?? [];
  const fetched = Object.values(data)
    .map((t) => t?.fetched_at)
    .filter(Boolean)
    .sort()
    .at(-1);

  return (
    <div className="space-y-3 sm:space-y-4">
      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        <Kpi icon={ClipboardList} label="Open taken" value={taken.length} tone={taken.length ? 'warn' : 'default'} hint={taken.length ? 'Te doen in AFAS' : 'Niets te doen'} />
        <Kpi icon={Inbox} label="Verzoeken in behandeling" value={lopend.length} hint={`${ingestuurd.length} ingestuurd in totaal`} />
        <Kpi icon={IdCard} label="ID-bewijs geldig tot" text={id?.geldig_tot ? formatDate(id.geldig_tot) : '–'} hint={id?.soort ?? 'Niet bekend in AFAS'} />
        <Kpi icon={Landmark} label="Loonheffingskorting" text={lhk?.loonheffingskorting ?? '–'} hint={lhk?.van ? `Sinds ${d(lhk.van)}` : undefined} />
      </div>

      <div className="grid gap-3 sm:gap-4 lg:grid-cols-2 [&>*]:min-w-0">
        <Card flush title="Open taken" icon={ClipboardList}>
          {taken.length ? (
            <div className="list border-t border-surface-line">
              {taken.map((t, i) => (
                <ListRow key={t.id ?? i} i={i} icon={ClipboardList} iconClass="text-amber-200" title={t.onderwerp} meta={`${t.actie ?? t.soort ?? ''}${t.datum ? `, sinds ${d(t.datum)}` : ''}`} />
              ))}
            </div>
          ) : (
            <EmptyState icon={BadgeCheck} title="Geen open taken" />
          )}
        </Card>

        <Card flush title="Verzuim" icon={Thermometer}>
          {verzuim.length ? (
            <div className="list border-t border-surface-line">
              {verzuim.map((v, i) => (
                <ListRow
                  key={`${v.van}-${i}`}
                  i={i}
                  icon={Thermometer}
                  title={v.van === v.tot || !v.tot ? d(v.van) : `${d(v.van)} t/m ${d(v.tot)}`}
                  meta={`${v.soort ?? 'Ziek'}${v.tot ? '' : ', nog open'}`}
                  value={v.pct != null ? `${v.pct}%` : undefined}
                />
              ))}
            </div>
          ) : (
            <EmptyState icon={BadgeCheck} title="Geen verzuim geregistreerd" />
          )}
        </Card>
      </div>

      <Card flush title="Ingestuurde verzoeken" icon={Inbox}>
        {ingestuurd.length ? (
          <>
            <div className="list border-t border-surface-line">
              {(allesIngestuurd ? ingestuurd : ingestuurd.slice(0, 5)).map((r, i) => (
                <div key={r.id ?? i} className="row enter" style={{ '--i': i } as React.CSSProperties}>
                  <Inbox className={`${ICON.sm} shrink-0 text-slate-400`} aria-hidden />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm text-slate-100">{r.onderwerp}</div>
                    <div className="truncate text-xs text-slate-400">
                      {r.soort}, {d(r.datum)}
                    </div>
                  </div>
                  <span className={statusCls(r.status)}>{r.status ?? 'Onbekend'}</span>
                </div>
              ))}
            </div>
            {ingestuurd.length > 5 && (
              <button type="button" className="row-link justify-center border-t border-surface-line text-sm text-slate-400" onClick={() => setAllesIngestuurd(!allesIngestuurd)}>
                {allesIngestuurd ? 'Minder tonen' : `Alle ${ingestuurd.length} tonen`}
              </button>
            )}
          </>
        ) : (
          <EmptyState icon={Inbox} title="Geen ingestuurde verzoeken" />
        )}
      </Card>

      <Card flush title="Dossier" icon={FolderOpen}>
        {dossier.length ? (
          <>
            <div className="list border-t border-surface-line">
              {(allesDossier ? dossier : dossier.slice(0, 8)).map((r, i) => (
                <ListRow key={`${r.id}-${i}`} i={i} icon={FileText} title={r.titel} meta={`${(r.soort ?? '').replace(/^Dossier - /, '')}${r.door && !/workato|SAP|T&R/i.test(r.door) ? `, ${r.door}` : ''}`} value={d(r.datum)} valueClass="text-slate-400" />
              ))}
            </div>
            {dossier.length > 8 && (
              <button type="button" className="row-link justify-center border-t border-surface-line text-sm text-slate-400" onClick={() => setAllesDossier(!allesDossier)}>
                {allesDossier ? 'Minder tonen' : `Alle ${dossier.length} tonen`}
              </button>
            )}
          </>
        ) : (
          <EmptyState icon={FolderOpen} title="Geen dossierstukken" />
        )}
      </Card>

      <Card flush title="Persoonlijk" icon={UserRound}>
        <div className="list border-t border-surface-line">
          <ListRow icon={IdCard} title="Identiteitsbewijs" meta={id ? `Uitgegeven ${d(id.uitgifte)}${id.land ? `, ${id.land}` : ''}` : 'Niet bekend in AFAS'} value={id?.geldig_tot ? `t/m ${d(id.geldig_tot)}` : '–'} />
          <ListRow icon={BadgeCheck} title="Verklaring omtrent gedrag" meta={vog.length ? `${vog.length} geregistreerd` : 'Niet geregistreerd in AFAS'} />
          <ListRow icon={FileText} title="Opleidingen, cursussen en competenties" meta={opleidingen.length ? `${opleidingen.length} geregistreerd` : 'Niets geregistreerd in AFAS'} />
          <ListRow
            icon={UserRound}
            title="Profielgegevens"
            meta={data.persoonlijk?.rows?.bijgewerkt?.length ? `Laatst overgenomen: ${data.persoonlijk.rows.bijgewerkt.join(', ')}` : 'Gelijk aan AFAS'}
            to="/instellingen"
          />
        </div>
      </Card>

      {fetched && (
        <p className="text-xs text-slate-500">
          Opgehaald {formatDate(fetched.replace(' ', 'T') + 'Z', 'dd-MM-yyyy HH:mm')}. Vanzelf om de twee dagen, of via Acties.
        </p>
      )}
    </div>
  );
}
